import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const UsageLogsTable = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [actionTypes, setActionTypes] = useState([]);
  const [emailDraft, setEmailDraft] = useState('');
  const [email, setEmail] = useState('');
  const [actionType, setActionType] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async (p, em, at) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 20 });
      if (em) params.set('email', em);
      if (at) params.set('action_type', at);
      const r = await axios.get(`${API_URL}/api/admin/usage-logs?${params}`);
      setLogs(r.data.logs || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
      setActionTypes(r.data.action_types || []);
    } catch {
      toast.error('載入使用記錄失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(page, email, actionType);
  }, [page, email, actionType, fetchLogs]);

  const search = (e) => {
    e.preventDefault();
    setPage(1);
    setEmail(emailDraft.trim());
  };

  return (
    <Card className="p-5" data-testid="usage-logs-table">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <h3 className="font-semibold text-sm text-neutral-900">
          最近使用記錄 <span className="text-neutral-400 font-normal">· 共 {total} 筆</span>
        </h3>
        <div className="flex items-center gap-2">
          <form onSubmit={search} className="flex items-center gap-1.5">
            <input
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              placeholder="搜尋用戶電郵"
              className="h-8 w-40 rounded-lg border border-neutral-200 px-2.5 text-xs outline-none focus:border-primary-400"
              data-testid="usage-logs-email-input"
            />
            <button
              type="submit"
              className="h-8 px-2.5 rounded-lg border border-neutral-200 text-neutral-500 hover:border-primary-400 hover:text-primary-600 transition-colors"
              data-testid="usage-logs-search-btn"
            >
              <Search className="w-3.5 h-3.5" />
            </button>
          </form>
          <select
            value={actionType}
            onChange={(e) => { setPage(1); setActionType(e.target.value); }}
            className="h-8 rounded-lg border border-neutral-200 px-2 text-xs text-neutral-600 outline-none focus:border-primary-400 max-w-[160px]"
            data-testid="usage-logs-type-select"
          >
            <option value="">全部類型</option>
            {actionTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-neutral-200">
              <th className="text-left py-2 px-3 text-neutral-500 font-medium">用戶</th>
              <th className="text-left py-2 px-3 text-neutral-500 font-medium">類型</th>
              <th className="text-left py-2 px-3 text-neutral-500 font-medium">模型</th>
              <th className="text-right py-2 px-3 text-neutral-500 font-medium">倍數</th>
              <th className="text-right py-2 px-3 text-neutral-500 font-medium">成本 US$</th>
              <th className="text-right py-2 px-3 text-neutral-500 font-medium">Credits</th>
              <th className="text-right py-2 px-3 text-neutral-500 font-medium">時間</th>
            </tr>
          </thead>
          <tbody className={loading ? 'opacity-50' : ''}>
            {logs.map((log, i) => (
              <tr key={log.id || i} className="border-b border-neutral-100 last:border-0 row-accent hover:bg-neutral-50 transition-colors">
                <td className="py-2 px-3 text-neutral-700">{log.user_email || '-'}</td>
                <td className="py-2 px-3 text-neutral-500">{log.action_type}</td>
                <td className="py-2 px-3 text-neutral-500 truncate max-w-[150px]">{log.model || '-'}</td>
                <td className="py-2 px-3 text-right text-neutral-500 tabular-nums">{log.multiplier ? `×${log.multiplier}` : '-'}</td>
                <td className="py-2 px-3 text-right text-neutral-500 tabular-nums">{log.cost_usd ? Number(log.cost_usd).toFixed(4) : '-'}</td>
                <td className={`py-2 px-3 text-right font-medium tabular-nums ${log.amount > 0 ? 'text-green-600' : 'text-neutral-800'}`}>
                  {Number(log.amount).toFixed(2)}
                </td>
                <td className="py-2 px-3 text-right text-neutral-400 whitespace-nowrap">
                  {log.created_at ? new Date(log.created_at).toLocaleString('zh-TW', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                </td>
              </tr>
            ))}
            {logs.length === 0 && !loading && (
              <tr><td colSpan={7} className="py-8 text-center text-neutral-400">沒有符合條件的記錄</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <span className="text-[11px] text-neutral-400">第 {page} / {pages} 頁</span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="h-8 px-3 rounded-lg border border-neutral-200 text-xs text-neutral-600 flex items-center gap-1 hover:border-primary-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="usage-logs-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> 上一頁
          </button>
          <button
            type="button"
            disabled={page >= pages || loading}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            className="h-8 px-3 rounded-lg border border-neutral-200 text-xs text-neutral-600 flex items-center gap-1 hover:border-primary-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="usage-logs-next"
          >
            下一頁 <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </Card>
  );
};

export default UsageLogsTable;
